import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useMemo,
} from "react";
import { lightTheme, darkTheme } from "../styles/colors";
import { weakenColor, pastelColor } from "../helpers/colorUtils";
import {
  THEME_STORAGE_KEYS,
  SESSION_STORAGE_KEYS,
  DEFAULT_VALUES,
} from "../constants/themeConstants";
import {
  saveToStorage,
  getFromStorage,
  removeMultipleFromStorage,
} from "../utils/storageUtils";
import {
  applyWhitelabelColors,
  updateFavicon,
  updatePageTitle,
  createTenantThemeObject,
  areThemesDifferent,
} from "../utils/themeUtils";

const ThemeContext = createContext({
  theme: lightTheme,
  isDarkMode: false,
  toggleTheme: () => {},
  tenantTheme: null,
  setTenantTheme: () => {},
  whitelabelEnabled: false,
  setWhitelabelEnabled: () => {},
  resetToDefaultTheme: () => {}
});

export const ThemeProvider = ({ children }) => {
  // Estados inicializados com valores do localStorage ou preferência do sistema
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = getFromStorage(THEME_STORAGE_KEYS.IS_DARK_MODE, null);
    // Se não houver salvo, o padrão é false (Claro), ignorando o sistema operacional por enquanto conforme solicitado
    return saved !== null ? saved : false;
  });

  const [theme, setTheme] = useState(() => {
    const saved = getFromStorage(THEME_STORAGE_KEYS.IS_DARK_MODE, null);
    const isDark = saved !== null ? saved : false;
    return isDark ? darkTheme : lightTheme;
  });

  const [tenantTheme, setTenantTheme] = useState(() =>
    getFromStorage(THEME_STORAGE_KEYS.TENANT_THEME, null)
  );

  const [whitelabelEnabled, setWhitelabelEnabled] = useState(() =>
    getFromStorage(THEME_STORAGE_KEYS.WHITELABEL_ENABLED, true)
  );

  const [themeInitialized, setThemeInitialized] = useState(() =>
    getFromStorage(THEME_STORAGE_KEYS.THEME_INITIALIZED, false)
  );

  // Função para atualizar o tema baseado no whitelabel
  const updateTheme = useCallback((isDark, tenantData, whitelabelActive) => {
    const baseTheme = isDark ? darkTheme : lightTheme;

    if (whitelabelActive && tenantData?.colors) {
      const customTheme = applyWhitelabelColors(baseTheme, tenantData.colors);
      setTheme(customTheme);

      // Atualizar favicon e título
      if (tenantData.fav_icon) updateFavicon(tenantData.fav_icon);
      if (tenantData.name) updatePageTitle(tenantData.name);
    } else {
      setTheme(baseTheme);
      updateFavicon(DEFAULT_VALUES.FAVICON_PATH);
      updatePageTitle(DEFAULT_VALUES.DEFAULT_TITLE);
    }
  }, []);

  // Toggle entre tema claro e escuro
  const toggleTheme = useCallback(() => {
    setIsDarkMode((prev) => {
      const newIsDarkMode = !prev;
      saveToStorage(THEME_STORAGE_KEYS.IS_DARK_MODE, newIsDarkMode);
      updateTheme(newIsDarkMode, tenantTheme, whitelabelEnabled);
      return newIsDarkMode;
    });
  }, [tenantTheme, whitelabelEnabled, updateTheme]);

  // Ativar/desativar whitelabel
  const toggleWhitelabel = useCallback(
    (enabled) => {
      setWhitelabelEnabled(enabled);
      saveToStorage(THEME_STORAGE_KEYS.WHITELABEL_ENABLED, enabled);
      updateTheme(isDarkMode, tenantTheme, enabled);
    },
    [isDarkMode, tenantTheme, updateTheme]
  );

  const tenantThemeRef = React.useRef(tenantTheme);
  const themeInitializedRef = React.useRef(themeInitialized);

  useEffect(() => {
    tenantThemeRef.current = tenantTheme;
  }, [tenantTheme]);

  useEffect(() => {
    themeInitializedRef.current = themeInitialized;
  }, [themeInitialized]);

  // Carregar tema do tenant via API
  const loadTenantTheme = useCallback(
    async (tenantId, forceReload = false) => {
      try {
        // Verificar se já está carregado e não é reload forçado
        if (
          tenantThemeRef.current?.id === tenantId &&
          !forceReload &&
          themeInitializedRef.current
        ) {
          return;
        }

        const { default: TenantService } = await import(
          "../services/tenantService"
        );
        const response = await TenantService.getThemeByIdentifier(tenantId);

        if (response?.data) {
          const tenantForContext = createTenantThemeObject(
            tenantId,
            response.data
          );

          // Aplicar apenas se for diferente
          if (areThemesDifferent(tenantThemeRef.current, tenantForContext)) {
            setTenantTheme(tenantForContext);
            saveToStorage(THEME_STORAGE_KEYS.TENANT_THEME, tenantForContext);
          }

          // Marcar como inicializado
          if (!themeInitializedRef.current) {
            setThemeInitialized(true);
            saveToStorage(THEME_STORAGE_KEYS.THEME_INITIALIZED, true);
          }
        }
      } catch (error) {
        console.error("Erro ao carregar tema do tenant:", error);
      }
    },
    [] // Dependências removidas graças aos refs
  );

  // Setar tema do tenant diretamente (ex: após login)
  const setTenantThemeData = useCallback((tenantData) => {
    if (!tenantData) return;

    // Usar identificador como ID se disponível, para compatibilidade com useThemeLoader
    const tenantId = tenantData.identificador || tenantData.id;
    
    const tenantForContext = createTenantThemeObject(
      tenantId,
      tenantData
    );

    // Sempre aplica o tema ao fazer login, ignorando verificação se é diferente
    // Isso garante que se outro usuário logar na mesma máquina, o tema será atualizado corretamente
    setTenantTheme(tenantForContext);
    saveToStorage(THEME_STORAGE_KEYS.TENANT_THEME, tenantForContext);

    // Garantir que whitelabel está ativo
    setWhitelabelEnabled(true);
    saveToStorage(THEME_STORAGE_KEYS.WHITELABEL_ENABLED, true);

    // Marcar como inicializado
    if (!themeInitializedRef.current) {
      setThemeInitialized(true);
      saveToStorage(THEME_STORAGE_KEYS.THEME_INITIALIZED, true);
    }
  }, []);

  // Aplicar tema baseado em dados do Company
  const applyCompanyTheme = useCallback(
    (companyData) => {
      const tenantForContext = {
        // Mantém o identificador do tenant já carregado: com um id diferente, o useThemeLoader
        // entende que o tema não é o do tenant e recarrega do servidor, sobrescrevendo a cor aplicada.
        id: tenantTheme?.id || "company-theme",
        name: companyData.name || "Sua Empresa",
        colors: {
          primary: companyData.primaryColor || DEFAULT_VALUES.PRIMARY_COLOR,
          secondary:
            companyData.secondaryColor || DEFAULT_VALUES.SECONDARY_COLOR,
          gradient1: companyData.primaryColor || DEFAULT_VALUES.PRIMARY_COLOR,
          gradient2:
            companyData.secondaryColor || DEFAULT_VALUES.SECONDARY_COLOR,
        },
        logo: companyData.companyLogo || null,
        fav_icon: companyData.companyLogo || DEFAULT_VALUES.FAVICON_PATH,
      };

      if (!whitelabelEnabled) {
        setWhitelabelEnabled(true);
        saveToStorage(THEME_STORAGE_KEYS.WHITELABEL_ENABLED, true);
      }

      if (areThemesDifferent(tenantTheme, tenantForContext)) {
        setTenantTheme(tenantForContext);
        saveToStorage(THEME_STORAGE_KEYS.TENANT_THEME, tenantForContext);
      }
    },
    [whitelabelEnabled, tenantTheme]
  );

  // Resetar para tema padrão
  const resetToDefaultTheme = useCallback(() => {
    setTenantTheme(null);
    setWhitelabelEnabled(false);
    saveToStorage(THEME_STORAGE_KEYS.TENANT_THEME, null);
    saveToStorage(THEME_STORAGE_KEYS.WHITELABEL_ENABLED, false);
    updateTheme(isDarkMode, null, false);
  }, [isDarkMode, updateTheme]);

  // Limpar completamente os dados do tema (logout)
  const clearThemeData = useCallback(() => {
    setTenantTheme(null);
    setWhitelabelEnabled(false);
    setThemeInitialized(false);

    // Limpar localStorage
    const keysToRemove = [
      ...Object.values(THEME_STORAGE_KEYS),
      ...Object.values(SESSION_STORAGE_KEYS),
    ];
    removeMultipleFromStorage(keysToRemove);

    updateTheme(isDarkMode, null, false);
  }, [isDarkMode, updateTheme]);

  // Tema final memoizado
  const finalTheme = useMemo(() => {
    const baseTheme = isDarkMode ? darkTheme : lightTheme;

    if (whitelabelEnabled && tenantTheme?.colors?.primary) {
      return applyWhitelabelColors(baseTheme, tenantTheme.colors);
    }

    return baseTheme;
  }, [isDarkMode, whitelabelEnabled, tenantTheme?.colors]);

  // Aplicar tema persistido na inicialização
  useEffect(() => {
    const savedTenantTheme = getFromStorage(THEME_STORAGE_KEYS.TENANT_THEME);
    const savedWhitelabelEnabled = getFromStorage(
      THEME_STORAGE_KEYS.WHITELABEL_ENABLED,
      true
    );
    const savedIsDarkMode = getFromStorage(
      THEME_STORAGE_KEYS.IS_DARK_MODE,
      false
    );

    if (savedTenantTheme && savedWhitelabelEnabled) {
      const baseTheme = savedIsDarkMode ? darkTheme : lightTheme;
      const appliedTheme = applyWhitelabelColors(
        baseTheme,
        savedTenantTheme.colors
      );
      setTheme(appliedTheme);

      if (savedTenantTheme.fav_icon) updateFavicon(savedTenantTheme.fav_icon);
      if (savedTenantTheme.name) updatePageTitle(savedTenantTheme.name);
    }
  }, []);

  // Reagir quando o tema final muda
  useEffect(() => {
    setTheme(finalTheme);

    if (whitelabelEnabled && tenantTheme) {
      if (tenantTheme.fav_icon) updateFavicon(tenantTheme.fav_icon);
      if (tenantTheme.name) updatePageTitle(tenantTheme.name);
    } else {
      updateFavicon(DEFAULT_VALUES.FAVICON_PATH);
      updatePageTitle(DEFAULT_VALUES.DEFAULT_TITLE);
    }
  }, [finalTheme, whitelabelEnabled, tenantTheme]);

  const spacing = useMemo(() => ({ xs: 4, sm: 8, md: 12, lg: 16, xl: 24 }), []);

  const contextValue = useMemo(
    () => ({
      theme,
      isDarkMode,
      toggleTheme,
      tenantTheme,
      setTenantTheme,
      loadTenantTheme,
      setTenantThemeData,
      whitelabelEnabled,
      setWhitelabelEnabled: toggleWhitelabel,
      applyCompanyTheme,
      resetToDefaultTheme,
      clearThemeData,
      weakenColor,
      pastelColor,
      themeInitialized,
      spacing,
    }),
    [
      theme,
      isDarkMode,
      toggleTheme,
      tenantTheme,
      loadTenantTheme,
      whitelabelEnabled,
      toggleWhitelabel,
      applyCompanyTheme,
      resetToDefaultTheme,
      clearThemeData,
      themeInitialized,
      spacing,
      setTenantThemeData,
    ]
  );

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};
